import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionPlans, createSubscription, createClientSideCheckout } from '../services/stripeService';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';

const PricingPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canceled = searchParams.get('canceled');



  const handleSubscribe = async (planId: string, priceId: string) => {
    console.log('🔄 handleSubscribe called with:', { planId, priceId, user: !!user, userId: user?.id });
    
    if (!user) {
      console.error('❌ No user found');
      setError('Please log in to subscribe');
      return;
    }

    setLoading(planId);
    setError(null);

    try {
      console.log('🚀 Trying Edge Function first...');
      await createSubscription({
        priceId,
        userId: user.id,
        userEmail: user.email || '',
        userName: user.name || 'User'
      });
      console.log('✅ createSubscription completed');
    } catch (err: any) {
      console.error('❌ Edge Function failed, trying client-side checkout...', err);
      
      try {
        console.log('🔄 Fallback to client-side checkout...');
        await createClientSideCheckout({
          priceId,
          userId: user.id,
          userEmail: user.email || '',
          userName: user.name || 'User'
        });
        console.log('✅ Client-side checkout completed');
      } catch (clientErr: any) {
        console.error('❌ Both Edge Function and client-side failed:', clientErr);
        setError(clientErr?.message || 'Failed to start subscription process. Please try again.');
      }
    } finally {
      setLoading(null);
    }
  };

  return (
    <Box sx={{ py: 8, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box textAlign="center" mb={6}>
            <Typography variant="h2" component="h1" gutterBottom sx={{ color: 'white', fontWeight: 'bold' }}>
              Upgrade to Premium
            </Typography>
            <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
              Unlock your full potential with advanced health optimization tools
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Join thousands of users who have transformed their health journey
            </Typography>
          </Box>
        </motion.div>

        {canceled && (
          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography variant="body1">
              <strong>Subscription canceled</strong>
            </Typography>
            <Typography variant="body2">
              No worries! You can subscribe anytime to unlock premium features. Your account remains active with free features.
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}



        <Grid container spacing={4} justifyContent="center">
          {/* Free Plan */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <CardContent sx={{ flexGrow: 1, p: 4 }}>
                  <Typography variant="h4" component="h2" gutterBottom align="center">
                    Free
                  </Typography>
                  <Box textAlign="center" mb={3}>
                    <Typography variant="h2" component="span" sx={{ fontWeight: 'bold' }}>
                      $0
                    </Typography>
                    <Typography variant="h6" component="span" color="text.secondary">
                      /forever
                    </Typography>
                  </Box>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Basic goal tracking" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Limited talent tree access" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Basic progress tracking" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Limited lock-in" />
                    </ListItem>
                  </List>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    sx={{ mt: 3 }}
                    disabled
                  >
                    Current Plan
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Premium Plans */}
          {subscriptionPlans.map((plan, index) => (
            <Grid item xs={12} md={4} key={plan.id}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              >
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    position: 'relative',
                    ...(plan.id === 'annual' && {
                      border: '3px solid',
                      borderColor: 'primary.main',
                      transform: 'scale(1.05)',
                      zIndex: 1
                    })
                  }}
                >
                  {plan.id === 'annual' && (
                    <Chip
                      label="BEST VALUE"
                      color="primary"
                      icon={<StarIcon />}
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontWeight: 'bold'
                      }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1, p: 4 }}>
                    <Typography variant="h4" component="h2" gutterBottom align="center">
                      {plan.name}
                    </Typography>
                    <Box textAlign="center" mb={3}>
                      <Typography variant="h2" component="span" sx={{ fontWeight: 'bold' }}>
                        ${plan.price}
                      </Typography>
                      <Typography variant="h6" component="span" color="text.secondary">
                        /{plan.interval}
                      </Typography>
                      {plan.id === 'annual' && (
                        <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                          Save $42.88 per year
                        </Typography>
                      )}
                    </Box>
                    <List>
                      {plan.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex}>
                          <ListItemIcon>
                            <CheckCircleIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      sx={{ mt: 3 }}
                      onClick={() => handleSubscribe(plan.id, plan.priceId)}
                      disabled={loading === plan.id}
                      startIcon={loading === plan.id ? <CircularProgress size={20} /> : null}
                    >
                      {loading === plan.id ? 'Processing...' : `Subscribe to ${plan.name}`}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Box textAlign="center" mt={8}>
            <Typography variant="h5" gutterBottom sx={{ color: 'white' }}>
              Frequently Asked Questions
            </Typography>
            <Grid container spacing={4} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Can I cancel anytime?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Yes, you can cancel your subscription at any time. Your premium features will remain active until the end of your billing period.
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Is there a free trial?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    We offer a free plan with basic features. You can upgrade to premium at any time to unlock advanced functionality.
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default PricingPage; 